import * as React from 'react';
import { Switch as ShadcnSwitch } from '@/hen-ui/shadcn/switch';
import { Label } from '@/hen-ui/atomics/Label';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';
export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnSwitch> {
  label: string;
  error?: boolean;
  disabled?: boolean;
  checked?: boolean;
}

/**
 * Switch component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui switch (Radix UI Switch primitive) with HEN-specific
 * variants and design tokens. All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Basic usage
 * <Switch label="Default Switch" />
 *
 * @example
 * // Disabled Switch
 * <Switch label="Disabled Switch" disabled />
 *
 * @example
 * // Error Switch
 * <Switch label="Error Switch" error />
 */

export const Switch = React.forwardRef<
  React.ElementRef<typeof ShadcnSwitch>,
  SwitchProps
>(({ className, label, error, disabled, ...props }, ref) => {
  return (
    <Flex align='center' gap='xxs' direction='row'>
      <ShadcnSwitch
        ref={ref}
        id={label}
        aria-invalid={error}
        disabled={disabled}
        className={className}
        {...props}
      />
      <Label
        className={cn(
          error && 'text-error-500',
          disabled && 'opacity-50',
          'font-normal',
        )}
        htmlFor={label}
      >
        {label}
      </Label>
    </Flex>
  );
});

Switch.displayName = 'Switch';
